import _isEmpty from 'lodash/isEmpty';
import _isNil from 'lodash/isNil';
import PropTypes from 'prop-types';
import React, { useEffect, useMemo, useState } from 'react';
import Styles from './EntityDrawer.scss';
import EntityDrawerAttributeRow from './EntityDrawerAttributeRow';
import EntityDrawerAttributesSkeleton from './EntityDrawerAttributeSkeleton';
import EntityDrawerAttributesSection from './EntityDrawerAttributesSection';
import EntityDrawerProcessResultRow from './EntityDrawerProcessResultRow';
import EntityDrawerQcRow from './EntityDrawerQcRow';
import { renderIkeOfficeData } from './EntityDrawerUtils';

export enum AccordionType {
    BASE_DATA,
    QC,
    PROCESSES,
}

const EntityDrawerAttributes = (props) => {
    const {
        selectedEntity,
        isLoading,
        clientId,
        startBaseDataOpen,
        startQcOpen,
        startProcessesOpen,
        onAccordionChange,
    } = props;

    const genericData = useMemo(() => {
        const dataArray: any = [];

        if (_isNil(selectedEntity)) return dataArray;

        const entityGenericData = selectedEntity.generic_data[0];

        for (const prop in entityGenericData) {
            const val: any =
                prop.toLowerCase().indexOf('photo') > -1 ? (
                    <a
                        href={entityGenericData[prop]}
                        target="_blank"
                        rel="noreferrer"
                    >
                        Download Photo
                    </a>
                ) : (
                    entityGenericData[prop]
                );

            dataArray.push(
                <EntityDrawerAttributeRow
                    key={prop}
                    keyName={prop}
                    value={val}
                />
            );
        }

        return dataArray;
    }, [selectedEntity]);

    const qcData = useMemo(() => {
        if (_isNil(selectedEntity) || _isNil(selectedEntity.qc_results))
            return [];

        return selectedEntity.qc_results.map((result) => (
            <EntityDrawerQcRow qcData={result} key={result.QC_result_id} />
        ));
    }, [selectedEntity]);

    const processData = useMemo(() => {
        if (_isNil(selectedEntity) || _isNil(selectedEntity.process_results))
            return [];

        return selectedEntity.process_results.map((result, i) => (
            <EntityDrawerProcessResultRow
                processData={result}
                key={`pr${selectedEntity.entity_version_id}${i}`}
            />
        ));
    }, [selectedEntity]);

    const handleAccordionStateChange = (
        newState: boolean,
        accordionType: AccordionType
    ) => {
        if (!_isNil(onAccordionChange)) {
            onAccordionChange(newState, accordionType);
        }
    };

    const attributesHtml = isLoading ? (
        <EntityDrawerAttributesSkeleton />
    ) : (
        <React.Fragment>
            <span className={Styles.entityDrawerTitle}>PoleID: {clientId}</span>
            <div className={Styles.entityDrawerAttributesContainer}>
                <EntityDrawerAttributesSection
                    data={genericData}
                    selectedEntity={selectedEntity}
                    title={clientId}
                    startOpen={startBaseDataOpen}
                    onToggle={(newState) =>
                        handleAccordionStateChange(
                            newState,
                            AccordionType.BASE_DATA
                        )
                    }
                />

                {processData.length > 0 && (
                    <EntityDrawerAttributesSection
                        title={'Process Information'}
                        data={processData}
                        startOpen={startProcessesOpen}
                        onToggle={(newState) =>
                            handleAccordionStateChange(
                                newState,
                                AccordionType.PROCESSES
                            )
                        }
                    />
                )}

                {qcData.length > 0 && (
                    <EntityDrawerAttributesSection
                        title={'QC Results'}
                        data={qcData}
                        startOpen={startQcOpen}
                        onToggle={(newState) =>
                            handleAccordionStateChange(
                                newState,
                                AccordionType.QC
                            )
                        }
                    />
                )}
            </div>
        </React.Fragment>
    );

    return (
        <div
            id="entity-drawer-data-container"
            className={Styles.entityDrawerDataContainer}
        >
            {attributesHtml}
        </div>
    );
};

EntityDrawerAttributes.propTypes = {
    selectedEntity: PropTypes.any,
    isLoading: PropTypes.bool,
    clientId: PropTypes.string,
    startBaseDataOpen: PropTypes.bool,
    startQcOpen: PropTypes.bool,
    startProcessesOpen: PropTypes.bool,
    onAccordionChange: PropTypes.func,
};
export default EntityDrawerAttributes;
